"use client";

import type { Dispatch, SetStateAction } from "react";
import { useEffect, useMemo, useState } from "react";
import {
  buildGanttLayout,
  getFitPxPerDay,
} from "./layout-gantt";
import { parseMermaidGantt } from "./parse-mermaid-gantt";
import {
  selectRangeDaysForZoomSelection,
} from "./gantt-render-selectors";
import type { GanttLayout, ParsedGantt } from "./gantt-types";

const ROW_ENTRANCE_STAGGER_MS = 40;
const ENTRANCE_COMPLETE_DELAY_MS = 450;

export interface UseGanttLayoutControllerParams {
  content: string;
  timelineViewportWidth: number;
}

export interface UseGanttLayoutControllerResult {
  parsed: ParsedGantt | null;
  layout: GanttLayout | null;
  fitPxPerDay: number;
  effectivePxPerDay: number;
  pxPerDayOverride: number | null;
  setPxPerDayOverride: Dispatch<SetStateAction<number | null>>;
  collapsedSections: Set<number>;
  setCollapsedSections: Dispatch<SetStateAction<Set<number>>>;
  isEntranceComplete: boolean;
}

export function useGanttLayoutController({
  content,
  timelineViewportWidth,
}: UseGanttLayoutControllerParams): UseGanttLayoutControllerResult {
  const parsed = useMemo(() => parseMermaidGantt(content), [content]);
  const [pxPerDayOverride, setPxPerDayOverride] = useState<number | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<number>>(
    new Set<number>(),
  );
  const [isEntranceComplete, setIsEntranceComplete] = useState<boolean>(false);

  useEffect(() => {
    if (!parsed) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      setPxPerDayOverride(null);
      setCollapsedSections(new Set<number>());
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
    };
  }, [parsed]);

  const rangeDays = useMemo(() => {
    if (!parsed) {
      return 1;
    }

    return selectRangeDaysForZoomSelection({
      inputTasks: parsed.tasks,
    });
  }, [parsed]);

  const fitPxPerDay = useMemo(() => {
    return getFitPxPerDay(rangeDays, timelineViewportWidth);
  }, [rangeDays, timelineViewportWidth]);
  const effectivePxPerDay = pxPerDayOverride ?? fitPxPerDay;

  const layout = useMemo(() => {
    if (!parsed) {
      return null;
    }

    return buildGanttLayout({
      parsed,
      pxPerDay: effectivePxPerDay,
      collapsedSections,
      timelineViewportWidth,
    });
  }, [
    collapsedSections,
    effectivePxPerDay,
    parsed,
    timelineViewportWidth,
  ]);

  useEffect(() => {
    if (!layout) {
      return undefined;
    }

    const resetFrame = window.requestAnimationFrame(() => {
      setIsEntranceComplete(false);
    });
    const durationMs =
      layout.taskRows.length * ROW_ENTRANCE_STAGGER_MS + ENTRANCE_COMPLETE_DELAY_MS;
    const timer = window.setTimeout(() => {
      setIsEntranceComplete(true);
    }, durationMs);

    return () => {
      window.cancelAnimationFrame(resetFrame);
      window.clearTimeout(timer);
    };
  }, [layout]);

  return {
    parsed,
    layout,
    fitPxPerDay,
    effectivePxPerDay,
    pxPerDayOverride,
    setPxPerDayOverride,
    collapsedSections,
    setCollapsedSections,
    isEntranceComplete,
  };
}
